import { Box, Button, Icon, Typography } from "@mui/material";
import PreVendaIcon from "components/Icons/PreVendaIcon";
import DialogContext from "components/confirm-dialog-context";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import ConfiguracaoIcon from "pages/Venda/ConfiguracaoIcon";
import ShoppingCar from "pages/Venda/VendasIcon";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  resetDescontoAutorizador,
  setDeposito,
  setTabelaPreco,
} from "redux/features/configuracoes";
import { resetConsignado } from "redux/features/consignadoSlice";
import { resetDesconto } from "redux/features/descontoFixoSlice";
import { resetChavesReferenciadas } from "redux/features/notaReferenciadaSlice";
import { resetPagamento } from "redux/features/pagamentoSlice";
import { resetState } from "redux/features/stateSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { theme } from "theme";
import "./sidemenu.style.css";
import useFlowRoles from "hooks/useFlowRoles";

const SideMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const dialog = useContext(DialogContext);
  const pedido = useAppSelector((s) => s.state.pedido);
  const { token: tokenAutorizador } = useAppSelector(
    (e) => e.configuracoes.configuracaoDescontoAutorizador
  );
  const configuracaoBalcao = useAppSelector(
    (s) => s.configuracoes.configuracaoPedidoBalcao
  );
  const { caixaAberto } = useAppSelector((e) => e.caixa.caixaAtual);
  const { addError } = useSnackbarCustom();

  const { caixa, preVenda, venda, consignado } = useFlowRoles();
  const canAccessCaixa = caixa.canAbrirCaixa || caixa.canFecharCaixa || caixa.canOutrosMovimentos;
  const canAccessPreVenda = preVenda.canNovo || preVenda.canEditar || preVenda.canAcoes;
  const canAccessVendas =
    venda.canNovo ||
    venda.canEditar ||
    venda.acoes.canVisualizar ||
    venda.pagamento.canFinalizar ||
    venda.pagamento.canFinalizarOutros;
  const canAccessConsignado = consignado.canNovo || consignado.canEditar || consignado.canMovimentacoes;

  const limpaTudo = () => {
    dispatch(resetState());
    dispatch(resetDesconto());
    dispatch(resetPagamento());
    if (tokenAutorizador) {
      dispatch(resetDescontoAutorizador());
    }
    if (configuracaoBalcao.deposito) {
      dispatch(setDeposito(configuracaoBalcao.deposito));
    }
    if (configuracaoBalcao.tabelaPreco) {
      dispatch(setTabelaPreco(configuracaoBalcao.tabelaPreco));
    }
    dispatch(resetChavesReferenciadas());
    dispatch(resetConsignado());
  };

  const handleNavigate = (page: string) => {
    if (
      tokenAutorizador.length > 0 &&
      !pedido.itens.length &&
      !pedido.itensTroca.length
    ) {
      dispatch(resetDescontoAutorizador());
      return navigate(`/${page}`);
    }
    if (pedido.itens.length || pedido.itensTroca.length) {
      return dialog.confirm({
        title: "Atenção",
        message:
          "Você possui um pedido em aberto. Ao retornar, todo o processo será perdido. Deseja continuar? ",
        onConfirm: () => {
          limpaTudo();
          return navigate(`/${page}`);
        },
      });
    } else {
      limpaTudo();
      return navigate(`/${page}`);
    }
  };

  const handleResetAndNavigate = (url: string) => {
    if (!caixaAberto && url === "/financas") {
      return addError({
        message:
          "Para acessar o módulo finanças é necessário possuir caixa aberto.",
        id: "sem_caixa_aberto_error",
        persist: true,
        closeable: true,
      });
    }
    if (pedido.itens.length || pedido.itensTroca.length) {
      return dialog.confirm({
        title: "Atenção",
        message:
          "Você possui um pedido em aberto. Ao retornar, todo o processo será perdido. Deseja continuar? ",
        onConfirm: () => {
          limpaTudo();
          return navigate(url);
        },
      });
    } else {
      navigate(url);
    }
    limpaTudo();
    return navigate(url);
  };

  const iconColor = theme.palette.primary.main;

  const ButtonSideMenu = (props: {
    id?: string;
    label: string;
    onClick: () => void;
    children?: JSX.Element;
    iconClassName?: string;
    disabled?: boolean;
  }) => {
    return (
      <Button
        className="button-side-menu"
        id={props.id ? `${props.id}-button` : undefined}
        disabled={props.disabled}
        sx={{
          flexDirection: "column",
          aspectRatio: "4/3",
          minWidth: 0,
          width: "100%",
          maxWidth: "150px",
          zIndex: 20,
          "&.Mui-disabled": {
            filter: "grayscale(1)",
            opacity: 0.45,
          },
        }}
        onClick={() => {
          props.onClick();
          setIsOpen(false);
        }}
      >
        <Box>
          {!!props.children ? (
            props.children
          ) : (
            <i
              id={props.id ? `${props.id}-icon` : undefined}
              className={props.iconClassName}
              style={{ fontSize: 30, color: iconColor }}
            />
          )}
        </Box>
        <Typography
          id={props.id ? `${props.id}-label` : undefined}
          sx={{
            fontSize: "11px",
            marginTop: "-15px",
            color: "#203720",
            fontFamily: "Work Sans",
          }}
        >
          {props.label}
        </Typography>
      </Button>
    );
  };

  return (
    <div className={`helper-container ${isOpen ? "open" : ""}`}>
      <Box
        className="helper-content"
        sx={{
          gap: "7px",
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <ButtonSideMenu
          id="menu-configuracoes"
          label="Configurações"
          onClick={handleNavigate.bind(null, "configuracoes")}
        >
          <ConfiguracaoIcon
            id="sideMenuConfiguracoes"
            color={iconColor}
            fontSize="60px"
          />
        </ButtonSideMenu>

        <ButtonSideMenu
          id="menu-caixa"
          label="Caixa"
          disabled={!canAccessCaixa}
          onClick={handleNavigate.bind(null, "caixa")}
        >
          <img
            id={"caixa_icon"}
            src={"/icon/menu/caixa_com_escrita.svg"}
            alt=""
            style={{ width: "60px", height: "60px" }}
          />
        </ButtonSideMenu>

        <ButtonSideMenu
          id="menu-pre-venda"
          label="Pré-venda"
          disabled={!canAccessPreVenda}
          onClick={() => handleResetAndNavigate("/pre-venda")}
        >
          <PreVendaIcon
            id="side_menu_pre_venda_icon"
            color={iconColor}
            fontSize="60px"
          />
        </ButtonSideMenu>

        <ButtonSideMenu
          id="menu-vendas"
          label="Vendas"
          disabled={!canAccessVendas}
          onClick={handleNavigate.bind(null, "venda")}
        >
          <ShoppingCar
            id="sideMenuShoppingCar"
            color={iconColor}
            fontSize="60px"
          />
        </ButtonSideMenu>

        <ButtonSideMenu
          id="menu-consignado"
          label="Consignado"
          disabled={!canAccessConsignado}
          onClick={() => handleResetAndNavigate("/consignado")}
        >
          <img
            id={"consignado_icon"}
            src={"/icon/menu/consignado.svg"}
            alt=""
            style={{ width: "60px", height: "60px" }}
          />
        </ButtonSideMenu>

        {/* <ButtonSideMenu label='Finanças' onClick={() => handleResetAndNavigate('/financas')} iconClassName='fa-solid fa-money-check-dollar' /> */}
      </Box>
      <Box
        id="open_side_menu_btn"
        sx={{
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.default,
          "&::before": {
            boxShadow: `-7px 6px 0 3px ${theme.palette.background.default},
                                    inset 3px 0 2px -1px rgba(0, 0, 0, .05)`,
          },
          "&::after": {
            boxShadow: `-7px -6px 0 3px ${theme.palette.background.default},
                                    inset 3px 0 2px -1px rgba(0, 0, 0, .05);`,
          },
        }}
        className="btn-container"
      >
        <Button
          sx={{
            background: "red",
            width: "100%",
            height: "100%",
            zIndex: 12,
            minWidth: 0,
            color: "inherit",
            backgroundColor: "transparent",
            borderRadius: "0 50% 50% 0",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          disableRipple
          disableFocusRipple
          disableElevation
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <Icon
            id="side-menu-icon"
            className="fa-solid fa-chevron-right icon"
          ></Icon>
        </Button>
      </Box>
    </div>
  );
};

export default SideMenu;
